import { TOOL_COLORS, type ToolColor } from '../../utils/toolSettings'

/** How loose ends are flagged in the editor and shown while writing. */
export interface LooseEndSettings {
  /** Typing TK as a word flags a loose end. */
  tkEnabled: boolean
  /** More tokens that flag a loose end. A token with a space in it is a pair,
   *  like "[[ ]]", and whatever is typed between becomes the note. */
  customTokens: string[]
  /** Convert typed tokens into loose-end chips, or leave them as plain text and only list them. */
  tokenMode: 'convert' | 'detect'
  /** Shortcut that flags a loose end at the cursor or on the selection. */
  flagShortcut: string
  /** Shortcut that opens the idea capture bar. */
  captureShortcut: string
  /** How loose ends look while focus dimming is on. */
  focusVisibility: 'show' | 'dim' | 'hide'
  /** Offer a kind for each loose end. */
  kindsEnabled: boolean
  kinds: string[]
  /** The kind a new loose end starts with; '' for none. */
  defaultKind: string
  /** Flagging opens the note at once, or drops the loose end and carries on. */
  openNoteOnFlag: boolean
  /** How loose ends look while writing, focus dimming or not. */
  visibility: 'show' | 'dim' | 'hide'
  /** A pin's look: a stamped TK, the letters alone, or a dot. */
  pinStyle: 'stamp' | 'quiet' | 'dot'
  /** Flagged text's look. */
  spanStyle: 'dotted' | 'wavy' | 'highlight' | 'none'
  /** The color of loose ends in the page. */
  color: ToolColor
  /** How the Loose Ends tool groups its list at first. */
  groupBy: 'page' | 'kind' | 'age'
  /** How much text around a loose end the list shows. */
  excerpt: 'full' | 'short'
  showStats: boolean
  /** Quick Edit: the passage editable in place in the list. */
  quickEdit: boolean
  /** Loose ends this many days old are marked; 0 for never. */
  staleDays: number
  /** The Inbox of parked ideas, at the top of the tool. */
  showIdeas: boolean
  /** Where "Make a note" files a note: under the idea's page, or at the top of the current desk. */
  noteDestination: 'source' | 'desk'
  /** Clearing history asks a second time. */
  confirmClear: boolean
  /** DOCX and ODT exports carry loose ends as margin comments, unless turned off at export. */
  exportComments: boolean
  /** Days and entries of resolved history kept per page (at most 90 and 50). */
  historyDays: number
  historyMax: number
}

export const DEFAULT_LOOSE_END_SETTINGS: LooseEndSettings = {
  tkEnabled: true,
  customTokens: [],
  tokenMode: 'convert',
  flagShortcut: 'Mod+Shift+K',
  captureShortcut: 'Mod+Alt+K',
  focusVisibility: 'show',
  kindsEnabled: false,
  kinds: ['Gap', 'Check', 'Doubt'],
  defaultKind: '',
  openNoteOnFlag: true,
  visibility: 'show',
  pinStyle: 'stamp',
  spanStyle: 'dotted',
  color: 'accent',
  groupBy: 'page',
  excerpt: 'full',
  showStats: true,
  quickEdit: true,
  staleDays: 0,
  showIdeas: true,
  noteDestination: 'source',
  confirmClear: true,
  exportComments: true,
  historyDays: 90,
  historyMax: 50,
}

export function normalizeLooseEndSettings(raw: any): LooseEndSettings {
  const d = DEFAULT_LOOSE_END_SETTINGS
  const strings = (value: unknown, fallback: string[], max: number) => Array.isArray(value)
    ? [...new Set(value.filter((item): item is string => typeof item === 'string').map((item) => item.trim()).filter(Boolean))].slice(0, max)
    : [...fallback]
  const shortcut = (value: unknown, fallback: string) =>
    typeof value === 'string' && /^((Mod|Ctrl|Meta|Alt|Shift)\+)+[^+]+$/.test(value) ? value : fallback
  return {
    tkEnabled: raw?.tkEnabled !== false,
    customTokens: strings(raw?.customTokens, d.customTokens, 12),
    tokenMode: raw?.tokenMode === 'detect' ? 'detect' : 'convert',
    flagShortcut: shortcut(raw?.flagShortcut, d.flagShortcut),
    captureShortcut: shortcut(raw?.captureShortcut, d.captureShortcut),
    focusVisibility: ['show', 'dim', 'hide'].includes(raw?.focusVisibility) ? raw.focusVisibility : d.focusVisibility,
    kindsEnabled: raw?.kindsEnabled === true,
    kinds: strings(raw?.kinds, d.kinds, 12).map((kind) => kind.slice(0, 24)),
    defaultKind: typeof raw?.defaultKind === 'string' ? raw.defaultKind.trim().slice(0, 24) : d.defaultKind,
    openNoteOnFlag: raw?.openNoteOnFlag !== false,
    visibility: oneOf(raw?.visibility, ['show', 'dim', 'hide'] as const, d.visibility),
    pinStyle: oneOf(raw?.pinStyle, ['stamp', 'quiet', 'dot'] as const, d.pinStyle),
    spanStyle: oneOf(raw?.spanStyle, ['dotted', 'wavy', 'highlight', 'none'] as const, d.spanStyle),
    color: oneOf(raw?.color, TOOL_COLORS, d.color),
    groupBy: oneOf(raw?.groupBy, ['page', 'kind', 'age'] as const, d.groupBy),
    excerpt: raw?.excerpt === 'short' ? 'short' : 'full',
    showStats: raw?.showStats !== false,
    quickEdit: raw?.quickEdit !== false,
    staleDays: wholeIn(raw?.staleDays, 0, 365, d.staleDays),
    showIdeas: raw?.showIdeas !== false,
    noteDestination: raw?.noteDestination === 'desk' ? 'desk' : 'source',
    confirmClear: raw?.confirmClear !== false,
    exportComments: raw?.exportComments !== false,
    historyDays: wholeIn(raw?.historyDays, 1, 90, d.historyDays),
    historyMax: wholeIn(raw?.historyMax, 1, 50, d.historyMax),
  }
}

function oneOf<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return options.includes(value as T) ? value as T : fallback
}

function wholeIn(value: unknown, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(value))
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback
}
